import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import JsBarcode from 'jsbarcode';
import StockController from './controllers/stock-controller';
import DepotController from './controllers/depot-controller';
import { usePermissionViewModel } from './permissions/viewmodel/permission-viewmodel';

const StocksPage = () => {
  const { hasPermission } = usePermissionViewModel('stock');
  const canEdit = hasPermission('edit') || hasPermission('write');
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [depots, setDepots] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [depotId, setDepotId] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [maxPage, setMaxPage] = useState(1);
  const [savingId, setSavingId] = useState(null);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    DepotController.getAllDepots()
      .then((list) => {
        const arr = list || [];
        setDepots(arr);
        // Auto-select the default depot (or the first one) so the list never aggregates "all depots".
        setDepotId((prev) => {
          if (prev) return prev;
          const def = arr.find((d) => d.isDefault) || arr[0];
          return def ? String(def.id) : '';
        });
      })
      .catch((e) => console.error('depots load error', e));
  }, []);

  const loadGroups = useCallback(async () => {
    if (!depotId) {
      // Wait until a specific depot is selected before loading the list.
      setGroups([]);
      setTotalItems(0);
      setMaxPage(1);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await StockController.fetchStocksGrouped(
        currentPage,
        pageSize,
        depotId,
        searchTerm
      );
      const list = (response.list || []).map((g) => ({
        ...g,
        lines: g.lines.map((l) => ({ ...l, savedQuantite: l.quantite }))
      }));
      setGroups(list);
      setTotalItems(response.total || 0);
      setMaxPage(response.maxPage || Math.max(1, Math.ceil((response.total || 0) / pageSize)));
      setExpanded({});
    } catch (e) {
      console.error(e);
      toast.error('Erreur lors du chargement du stock');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, depotId, searchTerm]);

  useEffect(() => { loadGroups(); }, [loadGroups]);

  const updateLineQuantity = (productId, lineId, value) => {
    setGroups((prev) => prev.map((g) =>
      g.productId !== productId
        ? g
        : { ...g, lines: g.lines.map((l) => l.id === lineId ? { ...l, quantite: value } : l) }
    ));
  };

  const saveLine = async (line) => {
    const q = parseInt(line.quantite, 10);
    if (isNaN(q) || q < 0) {
      toast.error('Quantité invalide');
      return;
    }
    if (line.savedQuantite !== undefined && line.savedQuantite === q) return;
    setSavingId(line.id);
    try {
      await StockController.updateQuantite(line.id, q);
      setGroups((prev) => prev.map((g) => ({
        ...g,
        lines: g.lines.map((l) => l.id === line.id ? { ...l, quantite: q, savedQuantite: q } : l)
      })));
      toast.success('Quantité mise à jour');
    } catch (e) {
      console.error(e);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSavingId(null);
    }
  };

  const toggleGroup = (pid) => setExpanded((prev) => ({ ...prev, [pid]: !prev[pid] }));

  const totalQty = (g) => g.lines.reduce((acc, l) => acc + (parseInt(l.quantite, 10) || 0), 0);

  // Build one barcode label per unit in stock (qty 2 => 2 labels), like the purchase print.
  const buildBarcodeTickets = (groupsToPrint, esc) => {
    const ticketsHtml = [];
    for (const g of groupsToPrint) {
      for (const l of g.lines) {
        const qty = parseInt(l.quantite, 10) || 0;
        const code = String(l.codeABarre || l.CodeABarre || '').trim();
        if (!code || qty <= 0) continue;
        const regularNum = Number(l.prixVenteTTC ?? l.PrixVenteTTC ?? 0);
        const enPromo = Boolean(l.enPromo ?? l.EnPromo) && Number(l.prixVentePromo ?? l.PrixVentePromo) > 0;
        const promoNum = Number(l.prixVentePromo ?? l.PrixVentePromo ?? 0);
        const effectiveNum = enPromo ? promoNum : regularNum;
        const priceText = Number.isFinite(effectiveNum) && effectiveNum > 0 ? `${effectiveNum.toFixed(2)} TND` : '';
        const oldText = enPromo && regularNum > effectiveNum ? `${regularNum.toFixed(2)} TND` : '';
        let dataUrl = '';
        try {
          const canvas = document.createElement('canvas');
          JsBarcode(canvas, code, {
            format: 'CODE128',
            width: 2,
            height: 95,
            fontSize: 20,
            textMargin: 2,
            fontOptions: 'bold',
            margin: 0,
            displayValue: true,
            background: '#ffffff',
            lineColor: '#000000',
          });
          dataUrl = canvas.toDataURL('image/png');
        } catch (e) {
          console.error('Barcode generation failed for', code, e);
        }
        const variantName = l.variantName || '';
        const refText = String(l.variantReference || g.productReference || '').trim();
        const ticket = `
          <div class="ticket">
            <div class="brand">Tendresse Store</div>
            ${refText ? `<div class="reference">${esc(refText)}</div>` : ''}
            ${variantName ? `<div class="variant">${esc(variantName)}</div>` : ''}
            <div class="name">${esc(g.productName || '')}</div>
            <div class="barcode-wrap">
              ${dataUrl
                ? `<img class="barcode" src="${dataUrl}" alt="${esc(code)}"/>`
                : `<div class="barcode-fallback">${esc(code)}</div>`}
            </div>
            <div class="price">${oldText ? `<span class="price-old">${esc(oldText)}</span> ` : ''}${esc(priceText)}</div>
          </div>`;
        for (let i = 0; i < qty; i++) ticketsHtml.push(ticket);
      }
    }
    return ticketsHtml;
  };

  const printBarcodes = (groupsToPrint, title) => {
    const esc = (s) => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const ticketsHtml = buildBarcodeTickets(groupsToPrint, esc);
    if (ticketsHtml.length === 0) {
      toast.warn('Aucun code-barres disponible en stock');
      return;
    }
    const printHtml = `<!doctype html><html><head><meta charset="utf-8"/>
      <title>Codes-barres - ${esc(title || '')}</title>
      <style>
        @page { size: 50mm 40mm; margin: 0; }
        html, body { margin: 0; padding: 0; background: #fff; }
        body { font-family: Arial, 'Helvetica', sans-serif; color: #000;
               -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .ticket {
          width: 50mm; height: 40mm;
          padding: 1.5mm 2mm 1.5mm 2mm;
          box-sizing: border-box;
          display: flex; flex-direction: column;
          align-items: center; justify-content: flex-start;
          page-break-after: always; break-after: page;
          overflow: hidden;
        }
        .ticket:last-child { page-break-after: auto; break-after: auto; }
        .brand { font-size: 11pt; font-weight: 900; text-align: center;
                 line-height: 1; letter-spacing: 1.2px;
                 font-family: 'Georgia', 'Times New Roman', serif;
                 font-style: italic;
                 width: 100%;
                 padding-bottom: 0.6mm;
                 border-bottom: 0.3mm solid #000; }
        .name { font-size: 6.5pt; font-weight: 700; text-align: center;
                line-height: 1.05; margin-top: 0.3mm; max-height: 4mm; overflow: hidden;
                word-break: break-word; }
        .variant { font-size: 8.5pt; font-weight: 900; text-align: center;
                   line-height: 1.05; margin-top: 0.4mm; overflow: visible;
                   word-break: break-word; }
        .reference { font-size: 9.5pt; font-weight: 900; text-align: center;
                     line-height: 1.05; margin-top: 0.6mm; color: #000;
                     overflow: visible; word-break: break-word; }
        .barcode-wrap { width: 100%; display: flex; justify-content: center;
                        align-items: center; margin-top: 0.8mm; }
        .barcode { width: 46mm; height: 17mm; display: block; }
        .barcode-fallback { font-family: 'Courier New', monospace;
                            font-size: 11pt; font-weight: 700; }
        .price { font-size: 12pt; font-weight: 900; text-align: center;
                 margin-top: 1mm; letter-spacing: 0.3px; white-space: nowrap; }
        .price-old { font-size: 8pt; font-weight: 700; text-decoration: line-through;
                     color: #555; margin-right: 1mm; }
      </style></head><body>
      ${ticketsHtml.join('')}
      </body></html>`;

    // Print via a hidden iframe (more reliable than window.open, which can
    // leave a blank about:blank popup if the browser blocks document.write).
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.setAttribute('aria-hidden', 'true');
    document.body.appendChild(iframe);
    const cleanup = () => { try { document.body.removeChild(iframe); } catch (e) { /* ignore */ } };
    iframe.onload = () => {
      try {
        const w = iframe.contentWindow;
        const imgs = Array.prototype.slice.call(w.document.images);
        const triggerPrint = () => {
          try { w.focus(); w.print(); } catch (e) { console.error(e); }
          w.onafterprint = () => setTimeout(cleanup, 200);
          setTimeout(cleanup, 60000);
        };
        let done = 0;
        const check = () => { if (done >= imgs.length) setTimeout(triggerPrint, 150); };
        if (imgs.length === 0) { setTimeout(triggerPrint, 150); return; }
        imgs.forEach((im) => {
          if (im.complete && im.naturalWidth > 0) { done++; }
          else {
            im.addEventListener('load', () => { done++; check(); });
            im.addEventListener('error', () => { done++; check(); });
          }
        });
        check();
      } catch (e) { console.error(e); cleanup(); }
    };
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(printHtml);
    doc.close();
  };

  const printGroupBarcodes = (g) => printBarcodes([g], g.productName);
  const printAllBarcodes = () => printBarcodes(groups, 'Stock');

  return (
    <div className="py-6 px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Stock</h1>
          <p className="mt-2 text-sm text-gray-700">
            Modifier directement la quantité en stock pour chaque variante par dépôt.
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            type="button"
            onClick={printAllBarcodes}
            disabled={groups.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#174540] text-white text-sm font-medium rounded-md shadow-sm hover:bg-[#0f322e] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 5v14M7 5v14M11 5v14M15 5v14M19 5v14" strokeLinecap="round" />
            </svg>
            Imprimer codes-barres
          </button>
        </div>
      </div>

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <select
          value={depotId}
          onChange={(e) => { setDepotId(e.target.value); setCurrentPage(1); }}
          className="block w-full sm:w-64 px-3 py-2 border border-gray-300 rounded-md shadow-sm sm:text-sm"
        >
          <option value="" disabled>Sélectionner un dépôt</option>
          {depots.map((d) => (
            <option key={d.id} value={d.id}>{d.name}{d.isDefault ? ' (défaut)' : ''}</option>
          ))}
        </select>
        <input
          type="text"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm sm:text-sm"
          placeholder="Rechercher par produit, référence, variante..."
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
        />
      </div>

      <div className="mt-6 bg-white shadow overflow-hidden sm:rounded-md">
        {loading ? (
          <div className="p-12 flex justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#174540]" />
          </div>
        ) : groups.length === 0 ? (
          <div className="p-12 text-center text-gray-500">Aucun stock trouvé.</div>
        ) : (
          <div className="divide-y divide-gray-200">
            {groups.map((g) => {
              const isOpen = !!expanded[g.productId];
              return (
                <div key={g.productId}>
                  <div className="w-full flex items-stretch bg-gray-50 hover:bg-gray-100">
                    <button
                      type="button"
                      onClick={() => toggleGroup(g.productId)}
                      className="flex-1 flex items-center justify-between px-6 py-3 text-left"
                    >
                      <div className="flex items-center gap-3">
                        <span className={`inline-block transform transition-transform ${isOpen ? 'rotate-90' : ''}`}>▶</span>
                        <div>
                          <div className="font-semibold text-gray-900">{g.productName}</div>
                          <div className="text-xs text-gray-500">{g.productReference}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-xs text-gray-500">{g.lines.length} ligne(s)</span>
                        {g.totalRetour > 0 && (
                          <span className="px-2 py-1 text-xs font-medium bg-amber-500 text-white rounded">
                            Retour: {g.totalRetour}
                          </span>
                        )}
                        <span className="px-2 py-1 text-xs font-medium bg-[#174540] text-white rounded">
                          Total: {totalQty(g)}
                        </span>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => printGroupBarcodes(g)}
                      title="Imprimer les codes-barres"
                      className="px-4 flex items-center text-[#174540] hover:bg-[#174540] hover:text-white transition-colors border-l border-gray-200"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 5v14M7 5v14M11 5v14M15 5v14M19 5v14" strokeLinecap="round" />
                      </svg>
                    </button>
                  </div>

                  {isOpen && (
                    <div className="overflow-x-auto">
                      <table className="min-w-full">
                        <thead className="bg-white">
                          <tr className="text-xs text-gray-500 uppercase">
                            <th className="px-6 py-2 text-left font-medium w-1/4">Variante</th>
                            <th className="px-6 py-2 text-left font-medium">Référence</th>
                            <th className="px-6 py-2 text-left font-medium">Dépôt</th>
                            <th className="px-6 py-2 text-left font-medium">Quantité</th>
                            <th className="px-6 py-2 text-left font-medium">À retourner</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {g.lines.length === 0 ? (
                            <tr>
                              <td colSpan={5} className="px-6 py-4 text-sm text-gray-500 text-center">
                                Aucune ligne de stock pour ce produit.
                              </td>
                            </tr>
                          ) : g.lines.map((l) => (
                            <tr key={l.id}>
                              <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                                {l.variantName || (g.hasVariants ? '—' : <span className="text-gray-400 italic">Sans variante</span>)}
                              </td>
                              <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700">
                                {l.variantReference || g.productReference}
                              </td>
                              <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700">{l.depotName || '—'}</td>
                              <td className="px-6 py-3 whitespace-nowrap">
                                <input
                                  type="number"
                                  min="0"
                                  value={l.quantite}
                                  onChange={(e) => updateLineQuantity(g.productId, l.id, e.target.value)}
                                  onBlur={() => { if (canEdit) saveLine(l); }}
                                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
                                  disabled={savingId === l.id || !canEdit}
                                  title={canEdit ? '' : 'Vous n’avez pas la permission'}
                                  className="w-24 px-2 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                />
                                {savingId === l.id && (
                                  <span className="ml-2 text-xs text-gray-500">...</span>
                                )}
                              </td>
                              <td className="px-6 py-3 whitespace-nowrap text-sm">
                                {l.quantiteRetour > 0 ? (
                                  <span className="inline-block px-2 py-1 text-xs font-medium bg-amber-100 text-amber-800 rounded">
                                    {l.quantiteRetour}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">0</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {maxPage > 1 && (
        <div className="mt-4 flex justify-between items-center">
          <span className="text-sm text-gray-600">
            {totalItems} produit(s) — Page {currentPage} / {maxPage}
          </span>
          <div className="space-x-2">
            <button
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage(currentPage - 1)}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Précédent
            </button>
            <button
              disabled={currentPage >= maxPage}
              onClick={() => setCurrentPage(currentPage + 1)}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Suivant
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StocksPage;
